import React from 'react';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: 'danger' | 'warning' | 'info';
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  variant = 'warning'
}) => {
  if (!isOpen) return null;

  const variantStyles = {
    danger: {
      icon: '⚠️',
      confirmBg: 'bg-red-500/20 border-red-500/50 text-red-300 hover:bg-red-500/30',
      titleColor: 'text-red-300'
    },
    warning: {
      icon: '⚠️',
      confirmBg: 'bg-yellow-500/20 border-yellow-500/50 text-yellow-300 hover:bg-yellow-500/30',
      titleColor: 'text-yellow-300'
    },
    info: {
      icon: 'ℹ️',
      confirmBg: 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/30',
      titleColor: 'text-cyan-300'
    }
  };

  const style = variantStyles[variant];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onCancel}
      />
      
      {/* Dialog */}
      <div className="relative bg-slate-800 rounded-lg shadow-2xl max-w-md w-full border border-white/10">
        <div className="p-6 space-y-4">
          {/* Title */}
          <div className="flex items-center gap-3">
            <span className="text-2xl">{style.icon}</span>
            <h3 className={`text-lg font-bold ${style.titleColor}`}>
              {title}
            </h3>
          </div>
          
          {/* Message */}
          <p className="text-slate-300 leading-relaxed">
            {message}
          </p>
          
          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-2.5 bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 hover:text-white rounded-lg font-medium transition-colors"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className={`flex-1 px-4 py-2.5 border rounded-lg font-medium transition-colors ${style.confirmBg}`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
